import React, { useState } from "react";
import { Trophy, Heart, Star, Medal } from "lucide-react";
import Panda from "./Panda";
import { bambooThisWeek } from "../lib/streak";
import { useAuth } from "../context/AuthContext";

const RANK_COLORS = ["#f3c932", "#c0c7d1", "#d9a066"];

// Friendly sibling leaderboard: bamboo this week (Mon-Sun) or all-time.
const Leaderboard = ({ children }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState("week");
  if (children.length < 2) return null;

  const rows = children
    .map((c) => ({ child: c, week: bambooThisWeek(c.bambooLog), total: c.bamboo || 0 }))
    .map((r) => ({ ...r, score: mode === "week" ? r.week : r.total }))
    .sort((a, b) => b.score - a.score || a.child.panda.name.localeCompare(b.child.panda.name));
  const top = rows[0].score;
  const allZero = top === 0;

  const cheer = (id) => {
    const card = document.getElementById(`child-${id}`);
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => document.getElementById(`cheer-input-${id}`)?.focus({ preventScroll: true }), 600);
  };

  return (
    <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8" data-testid="leaderboard">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="w-12 h-12 rounded-2xl bg-[#fff8e1] text-[#8a6d00] flex items-center justify-center flex-shrink-0"><Trophy /></span>
        <div className="flex-1">
          <h3 className="font-bold text-[#0c4a6e] text-xl" style={{ fontFamily: "Literata, serif" }}>Family bamboo board</h3>
          <p className="text-gray-600 text-sm mt-1">{mode === "week" ? "Bamboo earned since Monday. Every lesson step counts - cheer each other on!" : "All the bamboo ever earned. Steady wins the race."}</p>
        </div>
        <div className="inline-flex rounded-full bg-[#e9f9fd] p-1" data-testid="leaderboard-toggle">
          {[["week", "This week"], ["all", "All time"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setMode(id)} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${mode === id ? "bg-[#1b8bc9] text-white shadow" : "text-[#1b8bc9] hover:bg-white"}`} data-testid={`leaderboard-${id}`}>{label}</button>
          ))}
        </div>
      </div>

      <ol className="mt-6 space-y-3" data-testid="leaderboard-rows">
        {rows.map((r, i) => {
          const pct = top ? Math.max(6, Math.round((r.score / top) * 100)) : 6;
          const leader = !allZero && r.score === top;
          return (
            <li key={r.child.id} className={`flex items-center gap-3 sm:gap-4 rounded-2xl px-3 py-2.5 ${leader ? "bg-[#fff8e1]" : "bg-[#f7fdfe]"}`} data-testid={`leaderboard-row-${r.child.id}`}>
              <span className="w-8 h-8 rounded-full flex items-center justify-center font-logo text-white text-base flex-shrink-0" style={{ background: RANK_COLORS[i] || "#c9ecf5" }}>
                {i < 3 && !allZero ? <Medal size={16} /> : i + 1}
              </span>
              <Panda size={44} accessory={r.child.panda.accessory} scarf={r.child.panda.scarf} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-[#0c4a6e] truncate">{r.child.panda.name}</p>
                  {leader && <span className="text-[10px] uppercase font-bold tracking-wider text-[#8a6d00] bg-white rounded-full px-2 py-0.5">leading</span>}
                </div>
                <div className="mt-1.5 h-3 rounded-full bg-white overflow-hidden border border-[#e9f9fd]">
                  <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: leader ? "#f3c932" : "#63c79a" }} />
                </div>
              </div>
              <span className="inline-flex items-center gap-1 font-bold text-[#2e8b57] text-sm whitespace-nowrap" data-testid={`leaderboard-score-${r.child.id}`}><Star size={14} fill="#2e8b57" /> {r.score}</span>
              {user && r.child.ownerId && (
                <button type="button" onClick={() => cheer(r.child.id)} className="inline-flex items-center gap-1 text-xs font-bold text-[#e5356f] bg-white border border-[#f9a8c9] rounded-full px-3 py-1.5 hover:bg-[#e5356f] hover:text-white transition-colors whitespace-nowrap" data-testid={`leaderboard-cheer-${r.child.id}`}><Heart size={12} /> Cheer</button>
              )}
            </li>
          );
        })}
      </ol>
      {allZero && <p className="mt-3 text-xs text-gray-500 italic" data-testid="leaderboard-empty">{mode === "week" ? "No bamboo yet this week - the first lesson step takes the lead!" : "No bamboo yet - finish a lesson step to earn some."}</p>}
    </div>
  );
};

export default Leaderboard;
